import React, { Fragment, useCallback, useContext, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { AsyncTextInputProps } from './types';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import TextInput from '../TextInput/TextInput';
import { ReactComponent as AsyncLoader } from '../assets/async-loader.svg';
import { SpinnerContainer, Icon } from './styles';
import BasicIcon from '../BasicIcon/BasicIcon';

const AsyncTextInput: React.FC<AsyncTextInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    readOnly,
    disabled,
    showClearIcon = true,
    testId,
    size,
    variant = 'underline',
    helperText,
    iconComponent,
    pattern,
    validating,
    readOnlyValidating,
    onBlur,
    onChange,
    onFocus,
    onMouseDown,
    onKeyDown,
  } = props;

  const { actualTheme } = useContext(ThemeContext);

  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const isValidating = useMemo(() => validating || (meta && meta.validating), [meta, validating]);
  const isReadOnly = useMemo(() => readOnly || disabled || (isValidating && readOnlyValidating), [
    readOnly,
    disabled,
    isValidating,
    readOnlyValidating,
  ]);

  const handleClear = useCallback(() => {
    if (input && !isReadOnly) {
      if (input.onChange) {
        input.onChange('');
      }

      if (input.onBlur) {
        input.onBlur();
      }
    }
  }, [input, isReadOnly]);

  const IconComponent = useMemo(() => {
    const icons = [];

    if (isValidating) {
      icons.push(
        <SpinnerContainer key="async-spinner-icon" size="medium" data-testid="spinner-test">
          <AsyncLoader />
        </SpinnerContainer>,
      );
    }

    if (showClearIcon && input.value) {
      const { grayB8, gray80, status } = actualTheme;
      const grayColor = disabled ? grayB8 : gray80;

      icons.push(
        <Icon
          disabled={isReadOnly}
          onClick={handleClear}
          data-testid={`${testId}-clear-icon`}
          key="async-clear-icon"
        >
          <BasicIcon size="medium" name="x" color={hasError ? status.error.color : grayColor} />
        </Icon>,
      );
    } else if (iconComponent) {
      icons.push(<Fragment key="custom-async-icon">{iconComponent}</Fragment>);
    }

    if (icons.length) {
      return icons;
    }

    return null;
  }, [
    actualTheme,
    disabled,
    handleClear,
    hasError,
    iconComponent,
    input.value,
    isReadOnly,
    isValidating,
    showClearIcon,
    testId,
  ]);

  return (
    <ThemeProvider theme={actualTheme}>
      <TextInput
        testId={testId}
        label={label}
        readOnly={isReadOnly}
        disabled={disabled}
        type="text"
        input={input}
        meta={meta}
        autoComplete="off"
        size={size}
        variant={variant}
        helperText={helperText}
        showClearIcon={false}
        placeholder={placeholder}
        iconComponent={IconComponent}
        pattern={pattern}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onMouseDown={onMouseDown}
        onKeyDown={onKeyDown}
      />
    </ThemeProvider>
  );
};

export default AsyncTextInput;
